import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { constructQueryParamName } from '../../../util/search';

import css from './CategoryPills.module.css';

/**
 * Shorter labels for the pill row, keyed by category id.
 *
 * The Console name is also the sidebar filter label and the listing breadcrumb,
 * where the long form is the right one — this shortens the pill only. An id that
 * no longer exists just falls back to the Console name.
 */
const PILL_LABELS = {
  MusikUnterhaltung: 'Musik',
};

/**
 * Work out which categories to offer as pills, and at which nesting level.
 *
 * A marketplace with one top-level category (ours is "Hochzeit") gets no value
 * from a pill that selects it — every listing is in it. In that case the useful
 * choice is one level down. With several top-level categories, those are the
 * useful choice instead.
 *
 * @param {Array} categories nested categories from categoryConfiguration
 * @returns {Object} `{ options, parentId, level }` — level is 1-based and matches
 *                   the categoryLevel<N> query param the options write to
 */
const resolvePillOptions = (categories = []) => {
  const [firstCategory] = categories;
  const hasSingleRoot = categories.length === 1 && firstCategory?.subcategories?.length > 0;

  return hasSingleRoot
    ? { options: firstCategory.subcategories, parentId: firstCategory.id, level: 2 }
    : { options: categories, parentId: null, level: 1 };
};

/**
 * A row of category pills above the search results.
 *
 * Mirrors the kategorie row on the home page: it makes the shape of the
 * catalogue visible at a glance, which matters most while any single category
 * is still thin. Duplicating the sidebar's category filter is deliberate —
 * the sidebar is where you go to refine, this is where you go to browse.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.categoryConfiguration config.categoryConfiguration
 * @param {Object} props.urlQueryParams currently active search params
 * @param {Function} props.onSelect called with the updated query params
 * @param {string} [props.className]
 * @returns {JSX.Element|null} null when there are no categories to show
 */
const CategoryPills = props => {
  const { categoryConfiguration, urlQueryParams, onSelect, className } = props;
  const intl = useIntl();
  const { categories, categoryLevelKeys = [], scope = 'public' } = categoryConfiguration || {};

  const { options, parentId, level } = resolvePillOptions(categories);

  if (!options || options.length === 0) {
    return null;
  }

  const paramNames = categoryLevelKeys.map(key => constructQueryParamName(key, scope));
  const activeParamName = paramNames[level - 1];
  const activeId = activeParamName ? urlQueryParams?.[activeParamName] : null;

  // Every level is always written, so switching to a shallower selection clears
  // the deeper ones rather than leaving an orphaned categoryLevel3 behind.
  const paramsForSelection = selectedId => {
    const selectedByLevel = [...(parentId ? [parentId] : []), selectedId];
    return paramNames.reduce(
      (acc, paramName, index) => ({ ...acc, [paramName]: selectedByLevel[index] || null }),
      {}
    );
  };

  const clearedParams = paramNames.reduce((acc, paramName) => ({ ...acc, [paramName]: null }), {});

  return (
    <nav
      className={classNames(css.root, className)}
      aria-label={intl.formatMessage({ id: 'CategoryPills.ariaLabel' })}
    >
      <ul className={css.list}>
        {options.map(option => {
          const isActive = activeId === option.id;
          return (
            <li key={option.id} className={css.item}>
              <button
                type="button"
                className={classNames(css.pill, { [css.pillActive]: isActive })}
                aria-pressed={isActive}
                onClick={() => onSelect(isActive ? clearedParams : paramsForSelection(option.id))}
              >
                {PILL_LABELS[option.id] || option.name}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default CategoryPills;
